import { Graphics, Container, ContainerChild, Text, TextStyle } from "pixi.js";
import { liveQuery, type Subscription } from "dexie";

import type {
  IStudioWidget,
  IStudioWidgetPlaylist,
  IStudioWidgetPlaylistRow,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { ensureFontReady } from "../../../../hooks/useGoogleFont";

import {
  computeLetterSpacingPx,
  computeLineHeightPx,
  resolveTextStyle,
} from "../../../Text/textLayout";

import { openDatabase } from "../../../../Utils/db";
import {
  buildLivePlaylistRows,
  buildPlaylistDesignRows,
} from "../../../Playlist/playlistRows";
import { getPlaylistDesign } from "../../../../data/playlistDesignsCatalog";
import { PlaylistDesignRenderer } from "./PlaylistDesignRenderer";

interface WidgetState {
  rowsContainer?: Container;
  liveSubscription?: Subscription;
  /** Set when `options.designId` resolves to a catalog design. */
  designRenderer?: PlaylistDesignRenderer;
}

/**
 * Pixi renderer for the Playlist widget. Always live: subscribes to a
 * Dexie `liveQuery` over the Playlist table and repaints rows whenever
 * the streamer adds / removes / reorders tracks in the Playlist panel.
 * The Pixi side can't use the React `usePlaylist` hook, but Dexie's plain
 * `liveQuery` gives the same reactive subscription without React.
 *
 * Layout:
 * - Rows stacked vertically with a shared `rowGap`. Each row is
 *   `prefix + separator` (right-padded to `prefixMinWidth` for tabular
 *   alignment) followed by `title`.
 * - `ensureFontReady` + style reassignment busts Pixi's sprite cache once
 *   the Google Font face decodes (same pattern as TimerWidget /
 *   CountdownWidget SimpleRenderer).
 * - Optional `backgroundColor` fills the whole widget bounding once at
 *   render time, independent of the live row updates.
 */
export class PlaylistWidgetRenderer {
  private widgets = new Map<string, WidgetState>();

  /** Stop the live subscription + free a widget id's prior render state. */
  private teardownWidget(id: string): void {
    const prev = this.widgets.get(id);
    if (prev) {
      prev.liveSubscription?.unsubscribe();
      prev.designRenderer?.destroy();
      this.widgets.delete(id);
    }
  }

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetPlaylist;
    const options = w.options;

    // Tear down any prior render state for this id (the factory hands us a fresh
    // container, but the liveQuery subscription + design texture must be freed).
    this.teardownWidget(widget.id);

    // ── Design-store path ──────────────────────────────────────────────────
    // When `options.designId` resolves to a catalog design, render that design's
    // card SVG (driven by the same playlist liveQuery) and skip the plain list.
    const design = getPlaylistDesign(options.designId);
    if (design) {
      const designRenderer = new PlaylistDesignRenderer(design);
      designRenderer.render(container, destination);
      const state: WidgetState = { designRenderer };
      this.widgets.set(widget.id, state);

      const observable = liveQuery(async () => {
        const db = await openDatabase();
        return db.playlist.toArray();
      });
      state.liveSubscription = observable.subscribe({
        next: (tracks) => {
          const rows = buildPlaylistDesignRows(tracks, {
            prefixFormat: options.prefixFormat,
            titleFormat: options.titleFormat,
            maxRows: options.maxRows,
          });
          designRenderer.update(rows);
        },
        error: (err) => {
          console.error(`[PlaylistWidget] design liveQuery error:`, err);
        },
      });
      return;
    }

    if (options.backgroundColor && options.backgroundColor !== "transparent") {
      const bg = new Graphics();
      bg.rect(0, 0, destination.width, destination.height).fill(
        options.backgroundColor,
      );
      container.addChild(bg);
    }

    // Sub-container for rows. Repaints clear/refill this without touching
    // the background graphic, so live updates don't flicker the bg.
    const rowsContainer = new Container();
    container.addChild(rowsContainer);

    const state: WidgetState = { rowsContainer };
    this.widgets.set(widget.id, state);

    // Initial paint comes from `liveQuery`'s first emission (the current
    // DB snapshot); subsequent emissions repaint on every Playlist mutation.
    const observable = liveQuery(async () => {
      const db = await openDatabase();
      return db.playlist.toArray();
    });
    state.liveSubscription = observable.subscribe({
      next: (tracks) => {
        const rows = buildLivePlaylistRows(tracks, {
          prefixFormat: options.prefixFormat,
          titleFormat: options.titleFormat,
          maxRows: options.maxRows,
        });
        this.paintRows(rowsContainer, rows, options, destination.width);
      },
      error: (err) => {
        console.error(`[PlaylistWidget] liveQuery error:`, err);
      },
    });
  }

  private paintRows(
    container: Container,
    rows: IStudioWidgetPlaylistRow[],
    options: IStudioWidgetPlaylist["options"],
    destinationWidth: number,
  ): void {
    // Replace any previously-painted rows. `removeChildren(true)` would
    // also destroy them; we want destroy so GPU textures are freed when
    // a live update arrives. `destroy({ children: true })` cascades into
    // each `Text`'s sprite/texture cleanup.
    while (container.children.length > 0) {
      const child = container.removeChildAt(0);
      child.destroy({ children: true });
    }

    if (rows.length === 0) return;

    const baseStyle = resolveTextStyle({
      fontFamily: options.fontFamily,
      fontSize: options.fontSize,
      fontColor: options.textColor,
      backgroundColor: options.backgroundColor,
      bold: options.bold,
      italic: options.italic,
      underline: options.underline,
      strike: options.strike,
      textAlign: options.textAlign,
      lineHeight: options.lineHeight,
      wordSpacing: options.wordSpacing,
    });

    const fontSize = baseStyle.fontSize;
    const lineHeightPx = computeLineHeightPx(fontSize, options.lineHeight ?? 1);
    const letterSpacingPx = computeLetterSpacingPx(
      options.wordSpacing,
      fontSize,
    );
    const rowGap = options.rowGap ?? fontSize * 0.4;
    const prefixMinWidth = options.prefixMinWidth ?? 0;
    const prefixColor = options.prefixColor ?? baseStyle.fontColor;
    const align = baseStyle.textAlign;

    const buildStyle = (color: string) =>
      new TextStyle({
        fontFamily: baseStyle.fontFamily,
        fontSize,
        fill: color,
        fontWeight: baseStyle.bold ? "bold" : "normal",
        fontStyle: baseStyle.italic ? "italic" : "normal",
        lineHeight: lineHeightPx,
        letterSpacing: letterSpacingPx,
      });

    const prefixTexts: Text[] = [];
    const titleTexts: Text[] = [];

    rows.forEach((row, i) => {
      const rowY = i * (lineHeightPx + rowGap);

      const prefixText =
        row.prefix && row.separator
          ? `${row.prefix} ${row.separator}`
          : (row.prefix ?? "");

      let prefixObj: Text | undefined;
      let prefixWidth = 0;
      if (prefixText) {
        prefixObj = new Text({
          text: prefixText,
          style: buildStyle(prefixColor),
        });
        prefixWidth = prefixObj.width;
      }

      const titleObj = new Text({
        text: row.title,
        style: buildStyle(baseStyle.fontColor),
      });

      const columnOffset = Math.max(prefixMinWidth, prefixWidth);

      if (align === "left" || !prefixText) {
        if (prefixObj) {
          prefixObj.anchor.set(0, 0);
          prefixObj.x = 0;
          prefixObj.y = rowY;
          container.addChild(prefixObj as unknown as ContainerChild);
          prefixTexts.push(prefixObj);
        }
        titleObj.anchor.set(0, 0);
        titleObj.x = prefixText ? columnOffset : 0;
        titleObj.y = rowY;
      } else if (align === "right") {
        titleObj.anchor.set(1, 0);
        titleObj.x = destinationWidth;
        titleObj.y = rowY;
        if (prefixObj) {
          prefixObj.anchor.set(1, 0);
          prefixObj.x = destinationWidth - titleObj.width;
          prefixObj.y = rowY;
          container.addChild(prefixObj as unknown as ContainerChild);
          prefixTexts.push(prefixObj);
        }
      } else {
        const rowWidth = (prefixObj ? prefixObj.width : 0) + titleObj.width;
        const startX = (destinationWidth - rowWidth) / 2;
        if (prefixObj) {
          prefixObj.anchor.set(0, 0);
          prefixObj.x = startX;
          prefixObj.y = rowY;
          container.addChild(prefixObj as unknown as ContainerChild);
          prefixTexts.push(prefixObj);
        }
        titleObj.anchor.set(0, 0);
        titleObj.x = startX + (prefixObj ? prefixObj.width : 0);
        titleObj.y = rowY;
      }

      container.addChild(titleObj as unknown as ContainerChild);
      titleTexts.push(titleObj);
    });

    // Bust Pixi's per-style sprite cache once the Google Font face is
    // decoded — see SimpleRenderer / TimerWidget for the same pattern.
    void ensureFontReady(
      baseStyle.fontFamily,
      fontSize,
      baseStyle.bold ? 700 : 400,
    ).then(() => {
      prefixTexts.forEach((t) => {
        t.style = buildStyle(prefixColor);
      });
      titleTexts.forEach((t) => {
        t.style = buildStyle(baseStyle.fontColor);
      });
    });
  }

  destroy(): void {
    for (const state of this.widgets.values()) {
      state.liveSubscription?.unsubscribe();
      state.designRenderer?.destroy();
    }
    this.widgets.clear();
  }
}
